import { InjectionKey } from "vue";
import { createStore, useStore as baseUseStore, Store } from 'vuex'
import {
  UserArtworkFetch,
  TemplateAndLabelFetch,
  EditorParamsFetch,
  LabelShapesFetch,
  LabelSizesFetch,
  CustomLabelSizesFetch,
  MaterialsFetch,
  CurrentLabel,
  AccordianState,
  ColorPickrState,
  SettingsState,
  Dimensions,
  LabelSVG,
  CanvasDimensions,
  ClientDimensions,
} from "@/types";
import { actions } from "@/store/actions";
import { mutations } from "@/store/mutations";

declare global {
  interface Window {
    mobileCheck: any;
    safariCheck: any;
    opera: any;
    safari: any;
  }
}

/* eslint-disable no-useless-escape */
window.mobileCheck = function() {
  let check = false;
  (function(a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

const isMobile = window.mobileCheck();

export type State = {
  initialize: string;
  modal: string;
  showOnboarding: boolean;
  dimensions: Dimensions;
  userArtworkFetch: UserArtworkFetch;
  templateAndLabelFetch: TemplateAndLabelFetch;
  editorParamsFetch: EditorParamsFetch;
  labelShapesFetch: LabelShapesFetch;
  labelSizesFetch: LabelSizesFetch;
  customLabelSizesFetch: CustomLabelSizesFetch;
  materialsFetch: MaterialsFetch;
  accordianState: AccordianState;
  mountingLabel: boolean;
  currentLabel: CurrentLabel;
  currentLabelType: string;
  currentLabelMaterialId: number;
  currentLabelBgColor: string;
  currentActiveObject?: fabric.Object;
  canvasOrientation: string;
  colorPickrState: ColorPickrState;
  settingsState: SettingsState;
  labelSVG: LabelSVG;
  canvasDimensions: CanvasDimensions;
  clientDimensions: ClientDimensions;
  isMobile: boolean;
};

const dpi = 96;

const state: State = {
  initialize: "default",
  modal: "",
  showOnboarding: true,
  dimensions: {
    width: 4,
    height: 4,
    radius: 3,
    units: {
      dpi: dpi,
      offSet: 0.125,
      offSetPx: 0.125 * dpi,
    },
  },
  userArtworkFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  templateAndLabelFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  editorParamsFetch: {
    isFetching: false,
    payload: undefined,
    error: "",
  },
  labelShapesFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  labelSizesFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  customLabelSizesFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  materialsFetch: {
    isFetching: false,
    payload: [],
    error: "",
  },
  accordianState: {
    isOpen: false,
    tool: "",
    key: 0,
  },
  mountingLabel: false,
  currentLabel: {} as CurrentLabel,
  currentLabelType: "",
  currentLabelMaterialId: 0,
  currentLabelBgColor: "#fff",
  currentActiveObject: undefined,
  canvasOrientation: "default",
  colorPickrState: {
    isOpen: false,
    id: "",
    property: {
      name: "",
      value: "",
    },
    left: undefined,
    top: undefined,
  },
  settingsState: {
    guidelines: true,
    rulers: false,
    grid: true,
  },
  labelSVG: {
    group: {} as fabric.Group,
    fetching: false,
    component: "",
    withBleed: false,
  },
  canvasDimensions: {
    width: 0,
    height: 0,
  },
  clientDimensions: {
    width: 0,
    height: 0,
  },
  isMobile,
};

export const key: InjectionKey<Store<State>> = Symbol();

export const store = createStore<State>({
  state,
  mutations,
  actions,
  getters: {
    getState(state: State): State {
      return state;
    },
    initialize(state: State): string {
      return state.initialize;
    },
    modal(state: State): string {
      return state.modal;
    },
    showOnboarding(state: State): boolean {
      return state.showOnboarding;
    },
    canvasDimensions(state: State): CanvasDimensions {
      return state.canvasDimensions;
    },
    dimensions(state: State): Dimensions {
      // LabelDimensions
      return state.dimensions;
    },
    dpi(state: State): number {
      return state.dimensions.units.dpi;
    },
    mountingLabel(state: State): boolean {
      return state.mountingLabel;
    },
    appIsLoading(state: State): boolean {
      return (
        state.userArtworkFetch.isFetching ||
        state.templateAndLabelFetch.isFetching ||
        state.editorParamsFetch.isFetching ||
        state.labelShapesFetch.isFetching ||
        state.labelSizesFetch.isFetching ||
        state.customLabelSizesFetch.isFetching ||
        state.materialsFetch.isFetching ||
        state.mountingLabel
      );
    },
    bleedWidth(state: State): number {
      return (
        (state.dimensions.width + state.dimensions.units.offSet * 2) * state.dimensions.units.dpi
      );
    },
    bleedHeight(state: State): number {
      return (
        (state.dimensions.height + state.dimensions.units.offSet * 2) * state.dimensions.units.dpi
      );
    },
    safeZoneWidth(state: State): number {
      return (
        (state.dimensions.width - state.dimensions.units.offSet * 2) * state.dimensions.units.dpi
      );
    },
    safeZoneHeight(state: State): number {
      return (
        (state.dimensions.height - state.dimensions.units.offSet * 2) * state.dimensions.units.dpi
      );
    },
    labelWidth(state: State): number {
      return state.dimensions.width * state.dimensions.units.dpi;
    },
    labelHeight(state: State): number {
      return state.dimensions.height * state.dimensions.units.dpi;
    },
    labelRadius(state: State): number {
      return state.dimensions.radius * state.dimensions.units.dpi;
    },
    offSet(state: State): number {
      return state.dimensions.units.offSet;
    },
    userArtworkFetch(state: State): State["userArtworkFetch"]["payload"] {
      return state.userArtworkFetch.payload;
    },
    templateAndLabel(state: State): State["templateAndLabelFetch"]["payload"] {
      return state.templateAndLabelFetch.payload;
    },
    editorParams(state: State): State["editorParamsFetch"]["payload"] {
      return state.editorParamsFetch.payload;
    },
    labelShapes(state: State): State["labelShapesFetch"]["payload"] {
      return state.labelShapesFetch.payload;
    },
    labelSizes(state: State): State["labelSizesFetch"]["payload"] {
      return state.labelSizesFetch.payload;
    },
    customLabelSizes(state: State): State["customLabelSizesFetch"]["payload"] {
      return state.customLabelSizesFetch.payload;
    },
    materials(state: State): State["materialsFetch"]["payload"] {
      return state.materialsFetch.payload;
    },
    currentLabel(state: State): State["currentLabel"] {
      return state.currentLabel;
    },
    currentLabelType(state: State): State["currentLabelType"] {
      return state.currentLabelType;
    },
    currentLabelMaterialId(state: State): State["currentLabelMaterialId"] {
      return state.currentLabelMaterialId;
    },
    currentLabelBgColor(state: State): State["currentLabelBgColor"] {
      return state.currentLabelBgColor;
    },
    labelSVG(state: State): State["labelSVG"] {
      return state.labelSVG;
    },
    canvasOrientation(state: State): State["canvasOrientation"] {
      return state.canvasOrientation;
    },
    currentActiveObject(state: State): State["currentActiveObject"] {
      return state.currentActiveObject;
    },
    accordianState(state: State): State["accordianState"] {
      return state.accordianState;
    },
    colorPickrState(state: State): State["colorPickrState"] {
      return state.colorPickrState;
    },
    settingsState(state: State): State["settingsState"] {
      return state.settingsState;
    },
    clientDimensions(state: State): State["clientDimensions"] {
      return state.clientDimensions;
    },
    isMobile(state: State): boolean {
      return state.isMobile;
    },
  },
});